import { useEffect } from "react";

import dynamic from "next/dynamic";
import { MdDragHandle } from "react-icons/md";
import { useBoolean } from "usehooks-ts";

import { Box, Center, Flex, HStack, Icon, IconButton, TabList, Tabs } from "@chakra-ui/react";

import { Panel, PanelResizeHandle } from "@/components/factories/react-resizable-panels";
import { CloseIcon } from "@/components/shared/ReleaseIcons";
import TerminalTab from "@/components/TerminalTab";
import { useCurrentUser } from "@/providers/useCurrentUser";
import { useTerminals } from "@/providers/useTerminals";

const Terminal = dynamic(import("@/components/Terminal"), { ssr: false });

function TerminalPanel() {
  const {
    panelTerminals,
    terminalTabIndex,
    terminalPanelRef,
    collapseTerminalPanel,
    expandTerminalPanel,
    setTerminalTabIndex,
  } = useTerminals();
  const { value: dragging, setValue: setDragging } = useBoolean(false);

  const activeStyles = { color: "text.darkest", boxShadow: "0 -1px 1px #A9A7FF" };

  useEffect(() => {
    if (terminalTabIndex >= panelTerminals.length) {
      setTerminalTabIndex(panelTerminals.length - 1);
    }
  }, [panelTerminals.length, setTerminalTabIndex, terminalTabIndex]);

  const { currentUser } = useCurrentUser();

  if (!currentUser?.isEmployee || panelTerminals.length === 0) return null;

  return (
    <>
      <PanelResizeHandle
        _hover={activeStyles}
        bgColor="background.darker"
        position="relative"
        onDragging={setDragging}
        {...(dragging ? activeStyles : { boxShadow: "0 -1px 1px #EAE8ED" })}
      >
        <Center>
          <Icon as={MdDragHandle} />
        </Center>
      </PanelResizeHandle>
      <Panel
        ref={terminalPanelRef}
        collapsible
        id="bottomPanel"
        minSizePixels={140}
        // These account for the transitions when dragging the panel to/from a collapsed state
        onCollapse={collapseTerminalPanel}
        onExpand={expandTerminalPanel}
      >
        <Tabs h="full" index={terminalTabIndex} variant="enclosed-colored" w="full">
          <Flex direction="column" h="full">
            <Flex alignItems="center" bgColor="background.darker" justifyContent="space-between" w="full">
              <TabList pt={1}>
                {panelTerminals.map(panelTerminal => (
                  <TerminalTab key={`tab-${panelTerminal.terminalId}`} terminal={panelTerminal} />
                ))}
              </TabList>
              <HStack gap={0} mr={2}>
                <IconButton
                  aria-label="close-terminal-panel"
                  icon={<CloseIcon />}
                  size="lg"
                  title="Collapse Terminals"
                  variant="highlight"
                  onClick={collapseTerminalPanel}
                />
              </HStack>
            </Flex>
            {panelTerminals.map((panelTerminal, i) => (
              <Box
                key={`panel-${panelTerminal.terminalId}`}
                display={terminalTabIndex === i ? "block" : "none"}
                flex={1}
                h="full"
                overflow="hidden"
                p={0}
              >
                <Terminal terminal={panelTerminal} />
              </Box>
            ))}
          </Flex>
        </Tabs>
      </Panel>
    </>
  );
}

export default TerminalPanel;
